import qs from 'query-string';

// WorkOS AuthKit PKCE client (SPA flow, no backend), using the User Management
// API endpoints on the WorkOS auth server.
// https://workos.com/docs/reference/authkit/authentication/get-authorization-url#pkce

const AUTH_SERVER = 'https://auth.livereach.ai';
const CLIENT_ID = process.env.WORKOS_CLIENT_ID;
const ORGANIZATION_ID = 'org_01J9M5G20DBAG9115S491DC9ZF';
const PKCE_STORAGE_KEY = 'authkit:pkce';

const redirectUri = () => `${window.location.origin}/auth/callback`;

const base64UrlEncode = (bytes) => {
  let str = '';
  for (let i = 0; i < bytes.length; i += 1) {
    str += String.fromCharCode(bytes[i]);
  }
  return window.btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

const randomString = () => base64UrlEncode(window.crypto.getRandomValues(new Uint8Array(32)));

const sha256 = async (input) => {
  const digest = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(input));
  return new Uint8Array(digest);
};

export const decodeJwt = (jwt) => {
  try {
    const part = jwt.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(window.atob(part + '='.repeat((4 - (part.length % 4)) % 4)));
  } catch (error) {
    return null;
  }
};

// The app keys off token.expires (ms epoch); AuthKit access tokens carry it in
// the exp claim.
const normalizeToken = (data) => {
  const claims = decodeJwt(data.access_token) || {};
  const expires = claims.exp
    ? claims.exp * 1000
    : (data.expires_in || 300) * 1000 + new Date().getTime();
  return { ...data, expires };
};

const tokenRequest = async (params) => {
  const response = await window.fetch(`${AUTH_SERVER}/user_management/authenticate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
  let data = {};
  try {
    data = await response.json();
  } catch (error) {
    //
  }
  if (!response.ok) {
    const err = new Error(data.error_description || data.error || `Authentication failed (${response.status})`);
    err.code = data.error;
    err.status = response.status;
    throw err;
  }
  return normalizeToken(data);
};

export const isCallbackPath = pathname => pathname === '/auth/callback';

// Stash the PKCE verifier + state, then bounce to the hosted AuthKit login page.
export const login = async (returnTo) => {
  const state = randomString();
  const codeVerifier = randomString();
  const codeChallenge = base64UrlEncode(await sha256(codeVerifier));
  window.sessionStorage.setItem(
    PKCE_STORAGE_KEY,
    JSON.stringify({ state, codeVerifier, returnTo }),
  );

  window.location.assign(`${AUTH_SERVER}/user_management/authorize?${qs.stringify({
    response_type: 'code',
    client_id: CLIENT_ID,
    redirect_uri: redirectUri(),
    state,
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
    provider: 'authkit',
    organization_id: ORGANIZATION_ID,
    prompt: 'login',
  })}`);
};

// Called on /auth/callback: validate state and exchange the code for tokens.
export const handleCallback = async (search) => {
  const query = qs.parse(search);
  let stored = null;
  try {
    stored = JSON.parse(window.sessionStorage.getItem(PKCE_STORAGE_KEY));
  } catch (error) {
    //
  }
  window.sessionStorage.removeItem(PKCE_STORAGE_KEY);
  if (query.error) {
    throw new Error(query.error_description || query.error);
  }
  if (!query.code) {
    throw new Error('Missing authorization code');
  }
  if (!stored || !stored.state || stored.state !== query.state) {
    throw new Error('Login session expired, please sign in again');
  }
  const token = await tokenRequest({
    grant_type: 'authorization_code',
    client_id: CLIENT_ID,
    code: query.code,
    code_verifier: stored.codeVerifier,
    redirect_uri: redirectUri(),
  });
  return { token, returnTo: stored.returnTo || '/' };
};

let refreshPromise = null;

// Single-flight: refresh tokens are one-time-use, so concurrent 401s and the
// proactive timer must share one request.
//
// No organization_id here: WorkOS keeps the organization the session was
// created with. Login auto-selects a user's only membership without checking
// it against ORGANIZATION_ID, so users outside that org (e.g. the LRM Vision
// Audit labelers) sign in fine but would have every refresh rejected and be
// logged out when the access token first expires.
export const refreshSession = (refreshToken) => {
  if (!refreshPromise) {
    refreshPromise = tokenRequest({
      grant_type: 'refresh_token',
      client_id: CLIENT_ID,
      refresh_token: refreshToken,
    }).then(
      (token) => {
        refreshPromise = null;
        return token;
      },
      (error) => {
        refreshPromise = null;
        throw error;
      },
    );
  }
  return refreshPromise;
};

// Ends the AuthKit session; the session id lives in the access token's sid claim.
export const getLogoutUrl = (token) => {
  const claims = token && token.access_token ? decodeJwt(token.access_token) : null;
  if (!claims || !claims.sid) {
    return null;
  }
  return `${AUTH_SERVER}/user_management/sessions/logout?${qs.stringify({
    session_id: claims.sid,
    return_to: window.location.origin,
  })}`;
};
